import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import SponsorVideo from "core/components/SponsorVideo";
import LiveEvents from "core/components/LiveEvents";

const SHOW_AD = "SHOW_AD";

export default function SponsorLiveAdv({
  liveSeekBar,
  videoState,
  videoActions,
  content,
  rightholderId,
  sponsorVideoPlace,
  sponsorChatMessagePlace,
  fullscreenTarget,
  onChatAdv,
  impressionInterval,
  disabled,
  muted,
  vod,
}) {
  const [playedTime, setPlayedTime] = useState(0);

  // Короткое VOD-видео (меньше интервала) получает один мидролл посередине
  const [middleShown, setMiddleShown] = useState(false);

  useEffect(() => {
    if (!process.browser) return;
    if (videoState.status !== "playing") return;

    const update = () => {
      setPlayedTime((t) => t + 1);
    };

    const timer = setInterval(update, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [videoState.status]);

  // Время программы из HLS нужно только для live-событий. В плейлистах
  // загруженных видео его нет, и раньше из-за этой проверки мидроллы на VOD
  // не показывались совсем.
  if (!vod && !videoState.rawProgramDateTime) return null;
  if (videoState.isEnded) return null;
  if (!videoState.isStarted) return null;
  if (!process.browser) return null;

  const duration = videoState.duration || 0;
  const isShortVod = vod && duration > 0 && duration < impressionInterval;

  // Эфир во время рекламы идёт дальше без звука, а запись ставим на паузу,
  // иначе зритель пропустит кусок ролика
  const onStart = () => {
    if (vod) {
      videoActions.pause();
    } else {
      videoActions.toggleMute();
    }
  };

  const onFinish = () => {
    setPlayedTime(0);

    if (isShortVod) {
      setMiddleShown(true);
    }

    if (vod) {
      videoActions.play();
    } else {
      videoActions.toggleMute();
    }
  };

  const onNotFound = () => {
    setPlayedTime(0);

    // Для середины короткого ролика повторно не пытаемся
    if (isShortVod) {
      setMiddleShown(true);
    }
  };

  const onEvent = (e) => {
    if (e.event === SHOW_AD) {
      setPlayedTime(impressionInterval + 1);
    }
  };

  const videoPlace = "MIDROLL_IN_" + sponsorVideoPlace;
  const chatMessagePlace = sponsorChatMessagePlace
    ? "MIDROLL_IN_" + sponsorChatMessagePlace
    : null;

  // Длинное видео и эфир: раз в impressionInterval секунд просмотра.
  // Короткое VOD: один раз, когда позиция дошла до середины ролика.
  const isTimeForAd = isShortVod
    ? !middleShown && videoState.currentTime >= duration / 2
    : playedTime > impressionInterval;

  const showVideoAd = isTimeForAd && !disabled;

  return (
    <>
      {liveSeekBar && (
        <LiveEvents
          key={liveSeekBar.offset}
          contentId={content.id}
          currentTime={videoState.rawProgramDateTime}
          onEvent={onEvent}
        />
      )}
      {showVideoAd && (
        <SponsorVideo
          shareUrl={content?.shareUrl}
          chatMessagePlace={chatMessagePlace}
          videoPlace={videoPlace}
          contentId={content.id}
          sportId={content?.sport?.id}
          rightholderId={rightholderId}
          fullscreenTarget={fullscreenTarget}
          onStart={onStart}
          onFinish={onFinish}
          onNotFound={onNotFound}
          onChatAdv={onChatAdv}
          muted={muted}
        />
      )}
    </>
  );
}

SponsorLiveAdv.propTypes = {
  vod: PropTypes.bool,
  muted: PropTypes.bool,
  disabled: PropTypes.bool,
  onChatAdv: PropTypes.func,
  liveSeekBar: PropTypes.shape({
    offset: PropTypes.number.isRequired,
  }),
  videoState: PropTypes.shape({
    status: PropTypes.oneOf(["playing", "paused"]).isRequired,
    rawProgramDateTime: PropTypes.string,
    duration: PropTypes.number,
    currentTime: PropTypes.number,
    isEnded: PropTypes.bool,
    isStarted: PropTypes.bool,
  }).isRequired,
  videoActions: PropTypes.shape({
    toggleMute: PropTypes.func.isRequired,
    play: PropTypes.func,
    pause: PropTypes.func,
  }).isRequired,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    shareUrl: PropTypes.string.isRequired,
    sport: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  }),
  rightholderId: PropTypes.string.isRequired,
  sponsorChatMessagePlace: PropTypes.string,
  sponsorVideoPlace: PropTypes.string.isRequired,
  chatMessagePlace: PropTypes.string,
  fullscreenTarget: PropTypes.string.isRequired,
  impressionInterval: PropTypes.number.isRequired,
};
